import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectorRef,
  AfterViewInit,
  AfterViewChecked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogModule,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, Subject, combineLatest } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';

import {
  HubNotification,
  NotificationModalData,
  CreateNotificationRequest,
  UpdateNotificationRequest,
} from '@models/notification.model';
import { OrganizationDetails } from '@models/organization-details.model';
import { NotificationsAdminActions } from '@store/actions/notifications/notifications.actions';
import { selectNotificationsSaving } from '@store/selectors/notifications/notifications.selectors';
import { selectOrganizations } from '@store/selectors/organization-admin/organization-admin.selector';
import { selectIsMatrixPPAAdmin } from '@store/selectors/user/user.selectors';
import { OrganizationAdminActions } from '@store/actions/organization-admin/organization-admin.actions';

const ALL_ORGS_VALUE = -1;

interface NotificationForm {
  title: FormControl<string>;
  organizationIds: FormControl<number[]>;
  type: FormControl<string>;
  priority: FormControl<string>;
  body: FormControl<string>;
}

@Component({
  selector: 'app-notification-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    NgxMatSelectSearchModule,
  ],
  templateUrl: './notification-modal.component.html',
  styleUrl: './notification-modal.component.css',
})
export class NotificationModalComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly dialogRef = inject(MatDialogRef<NotificationModalComponent>);
  readonly data = inject<NotificationModalData>(MAT_DIALOG_DATA);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  saving$ = this.store.select(selectNotificationsSaving);
  isMatrixPPAAdmin$ = this.store.select(selectIsMatrixPPAAdmin);
  allOrgs$ = this.store.select(selectOrganizations);
  filteredOrgs$: Observable<OrganizationDetails[]>;

  readonly orgSearchControl = new FormControl<string>('');
  readonly form: FormGroup<NotificationForm>;

  availableTypes: string[] = [];
  isMatrixPPAAdmin = false;

  readonly ALL_ORGS_VALUE = ALL_ORGS_VALUE;
  readonly priorities = ['Low', 'Medium', 'High'];

  get isCreateMode(): boolean { return this.data.mode === 'create'; }
  get isEditMode(): boolean { return this.data.mode === 'edit'; }
  get isViewMode(): boolean { return this.data.mode === 'view'; }

  formatDate(dateStr: string | undefined): string {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  }

  get title(): string {
    if (this.isCreateMode) return 'Create Notification';
    if (this.isEditMode) return 'Edit Notification';
    return 'View Notification';
  }

  constructor() {
    this.form = this.buildForm();
    this.filteredOrgs$ = this.createFilteredOrgs();
  }

  ngOnInit(): void {
    this.store.dispatch(OrganizationAdminActions.loadOrganizations());

    this.isMatrixPPAAdmin$.pipe(takeUntil(this.destroy$)).subscribe((isAdmin) => {
      this.isMatrixPPAAdmin = isAdmin;
      this.availableTypes = isAdmin
        ? ['Admin', 'System', 'Other']
        : ['Admin', 'Other'];
    });

    this.actions$.pipe(
      ofType(
        NotificationsAdminActions.createNotificationSuccess,
        NotificationsAdminActions.updateNotificationSuccess
      ),
      takeUntil(this.destroy$)
    ).subscribe(() => this.dialogRef.close());
  }

  ngAfterViewInit(): void { this.cdr.detectChanges(); }
  ngAfterViewChecked(): void { this.cdr.detectChanges(); }

  onAllOrgsChange(checked: boolean): void {
    const orgControl = this.form.get('organizationIds');
    if (!orgControl) return;
    if (checked) {
      orgControl.setValue([ALL_ORGS_VALUE]);
    } else {
      orgControl.setValue([]);
    }
  }

  get isAllOrgsSelected(): boolean {
    return (this.form.get('organizationIds')?.value ?? []).includes(ALL_ORGS_VALUE);
  }

  onOrgSelectionChange(selectedIds: number[]): void {
    if (selectedIds.includes(ALL_ORGS_VALUE) && selectedIds.length > 1) {
      this.form.get('organizationIds')?.setValue([ALL_ORGS_VALUE]);
    }
  }

  onSave(): void {
    if (this.form.invalid) return;

    const { title, organizationIds, type, priority, body } = this.form.getRawValue();

    if (this.isCreateMode) {
      const request: CreateNotificationRequest = {
        title,
        organizationIds: organizationIds.includes(ALL_ORGS_VALUE) ? [] : organizationIds,
        type,
        priority,
        body,
        sendImmediately: true,
      };
      this.store.dispatch(NotificationsAdminActions.createNotification({ request }));
    } else if (this.isEditMode && this.data.notification) {
      const request: UpdateNotificationRequest = {
        id: this.data.notification.id,
        title,
        priority,
        body,
      };
      this.store.dispatch(NotificationsAdminActions.updateNotification({ request }));
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildForm(): FormGroup<NotificationForm> {
    const notification = this.data.notification;
    const isView = this.data.mode === 'view';
    const isEdit = this.data.mode === 'edit';

    return new FormGroup<NotificationForm>({
      title: new FormControl(
        { value: notification?.title ?? '', disabled: isView },
        { validators: [Validators.required, Validators.maxLength(128)], nonNullable: true }
      ),
      organizationIds: new FormControl(
        {
          value: notification
            ? (notification.organizationIds.length === 0 ? [ALL_ORGS_VALUE] : notification.organizationIds)
            : [],
          disabled: isView || isEdit,
        },
        { validators: isEdit || isView ? [] : [Validators.required], nonNullable: true }
      ),
      type: new FormControl(
        { value: notification?.type ?? '', disabled: isView || isEdit },
        { validators: [Validators.required], nonNullable: true }
      ),
      priority: new FormControl(
        { value: notification?.priority ?? '', disabled: isView },
        { validators: [Validators.required], nonNullable: true }
      ),
      body: new FormControl(
        { value: notification?.body ?? '', disabled: isView },
        { validators: [Validators.required, Validators.maxLength(4000)], nonNullable: true }
      ),
    });
  }

  private createFilteredOrgs(): Observable<OrganizationDetails[]> {
    return combineLatest([
      this.allOrgs$,
      this.orgSearchControl.valueChanges.pipe(startWith('')),
    ]).pipe(
      map(([orgs, searchTerm]) => {
        if (!orgs) return [];
        const active = orgs.filter((o) => o.statusID === 1000);
        if (!searchTerm) return active;
        return active.filter((o) => o.name.toLowerCase().includes(searchTerm.toLowerCase()));
      })
    );
  }
}
